import { QrCode02 } from '@untitledui/icons'
import { useEffect, useMemo, useRef } from 'react'
import { type RefObject } from 'react'

import { noop } from '@/lib/utils'
import { InfiniteScrollTrigger } from '@components/InfiniteScrollTrigger'
import PaginationInfo from '@components/PaginationInfo'
import { useDialog } from '@components/designSystem/Dialog/useDialog'
import { GtinScannerDialog } from '@components/productFinder/GtinScannerDialog'
import History from '@components/productFinder/History'
import ResultList from '@components/productFinder/ResultList'
import SearchInput from '@components/productFinder/SearchInput'
import { useProductFinderState } from '@components/productFinder/state'
import useHistory from '@components/productFinder/useHistory'
import { useInfiniteScroll } from '@hooks/useInfiniteScroll'
import { useIsWideLayout } from '@hooks/useMediaQuery'
import useNavigator from '@hooks/useNavigator'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useProductFinderContext } from '@routes/ProductFinder/Context'
import { useProductSearchInfinite } from '@state/product'

import ScrollToTopButton from '../designSystem/ScrollToTopButton'

interface ProductFinderProps {
  onBack?: () => void
}

function ProductFinder({ onBack }: ProductFinderProps) {
  const { t } = useTypedTranslation()
  const isWideLayout = useIsWideLayout()
  const { goToStoreMap } = useNavigator()
  const { searchState, setSearchState, selectedGTIN, setSelectedGTIN } = useProductFinderContext()

  const [history, addToHistory, removeFromHistory] = useHistory()
  const { showDialog: showGtinScanDialog } = useDialog(GtinScannerDialog, { onScan })

  const {
    searchInputValue,
    setSearchInputValue,
    activeSearchTerm,
    commitSearch,
    searchType,
    reset,
    showHistory,
    setShowHistory,
  } = useProductFinderState()

  const listContainerRef = useRef(null)

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductSearchInfinite({
    gtin: searchType === 'gtin' ? activeSearchTerm : undefined,
    term: searchType === 'term' ? activeSearchTerm : undefined,
  })

  const infiniteScrollRef = useInfiniteScroll({
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  })

  function selectProduct(gtin: string) {
    if (isWideLayout) {
      setSelectedGTIN(gtin)
    } else {
      goToStoreMap(gtin)
    }
  }

  function onScan(scannedGtin: string) {
    goToStoreMap(scannedGtin)
  }

  const items = useMemo(() => data?.pages.flatMap(page => page.items) ?? [], [data])
  const lastPage = data?.pages[data.pages.length - 1]
  const pagination = lastPage?.pagination

  useEffect(() => {
    if (!activeSearchTerm || items.length === 0) return

    addToHistory(activeSearchTerm)
  }, [activeSearchTerm, items, addToHistory])

  const searchInput = (
    <div className='mb-4 flex gap-2'>
      <div className='flex-1'>
        <SearchInput
          isInert={searchState !== 'open'}
          autoFocus={searchState === 'open' && !activeSearchTerm}
          placeholder={t('productFinder.placeholder')}
          value={searchInputValue}
          onChange={setSearchInputValue}
          onCancel={reset}
          onBack={onBack ?? noop}
          onSearch={() => commitSearch(searchInputValue)}
          onActivate={() => setSearchState('open')}
          onFocus={() => setShowHistory(true)}
        />
      </div>

      <button className={`
        flex aspect-square items-center justify-center rounded-full border border-border-primary bg-white p-2
        sm:hidden
      `} aria-label='scan-gtin'>
        <QrCode02 onClick={() => showGtinScanDialog()} />
      </button>
    </div>
  )

  if (searchState !== 'open') {
    return searchInput
  }

  return (
    <>
      {searchInput}

      {!showHistory && pagination && (
        <PaginationInfo currentCount={items.length} totalCount={pagination.total} />
      )}

      <div className='relative flex-1'>
        <ScrollToTopButton listContainerRef={listContainerRef} />

        <div ref={listContainerRef} className="absolute inset-0 overflow-y-auto">
          {!showHistory && (
            <>
              <ResultList
                isLoading={isLoading}
                items={items}
                selectGtin={selectProduct}
                selectedGtin={selectedGTIN}
                searchType={searchType}
                searchTerm={activeSearchTerm ?? ''}
              />

              <InfiniteScrollTrigger
                targetRef={infiniteScrollRef as unknown as RefObject<HTMLDivElement>}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </>
          )}

          {showHistory && (
            <History
              history={history}
              onEntrySelect={commitSearch}
              onEntryRemove={removeFromHistory}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default ProductFinder
